/**
 * Create / edit form for a budget rule. Client-side validation MIRRORS the
 * server's (budget-rules.ts §2.1) so the common mistakes never round-trip — the
 * server remains authoritative and its 400 is surfaced inline.
 *
 * A `cadence` rule's metric is IMPLIED by its kind (`silence_minutes`, spec
 * §2.1) and `period` is rejected for it, so neither field is offered: the form
 * cannot express — let alone submit — a `kind`/`metric` pair the server would
 * 400. See `CostMetric` for why that is a type property rather than a check.
 *
 * `kind`, `metric`, and `period` are LOCKED on edit: changing them server-side
 * would orphan the rule's dedup keys, so the API rejects them (spec §6.3) and
 * callers delete-and-recreate instead.
 *
 * A stored `webhook_secret` is never sent to the client — the form shows a
 * "secret is stored" hint and only PATCHes the field when the user types a new one.
 *
 * The scope is a PRODUCER (`scope_producer`, a Guardius identity_id). The old
 * `scope_platform` is retired: the server refuses it on create and on patch —
 * even as `null` — so this form never sends it. A rule stored before the cutover
 * may still carry one; the evaluator drops such a rule on every pass, and since
 * no patch can remove the field, the form says so and points at delete-and-
 * recreate. It never copies the platform name into the producer field, because
 * a platform name is not a producer address.
 */

import { useState } from 'react';
import {
  CADENCE_THRESHOLD_MAX_MINUTES, CADENCE_THRESHOLD_MIN_MINUTES,
  useCreateBudget, useUpdateBudget,
  type BudgetCreateInput, type BudgetKind, type BudgetMetric, type BudgetPatchInput,
  type BudgetPeriod, type BudgetRuleView,
} from '../lib/queries';

const NAME_MAX = 120;
/** Mirrors `SCOPE_PRODUCER_MAX` server-side. */
const SCOPE_PRODUCER_MAX = 64;
/** Mirrors `PRODUCER_FORBIDDEN_CHARS` (lib/lambdas/shared/producer.ts): the value
 *  is spliced into a sort key, so `#` would move a key boundary, `/` and `=` an
 *  S3 segment, and `* : { }` are permission-grammar characters. */
const PRODUCER_FORBIDDEN_CHARS = /[#/=*:{}\s]/;
const SECRET_MIN = 16;
const SECRET_MAX = 256;

/**
 * Every metric a USER may pick — i.e. `BudgetMetric` minus the one that is
 * implied by its kind. This is how the §2.1 biconditional
 * (`kind: 'cadence'` ⟺ `metric: 'silence_minutes'`) is made STRUCTURAL rather
 * than policed after the fact:
 *
 * - `FormValues.metric` is a `CostMetric`, so no form state can carry
 *   `silence_minutes` — the non-cadence direction cannot be violated even by a
 *   caller constructing `FormValues` by hand;
 * - `toCreateInput` DERIVES the metric from the kind for cadence, so the
 *   cadence direction cannot be violated either.
 *
 * The alternative — keeping one `metric` field and validating the pair — would
 * let the form produce a `400` for a pair the user never chose, which is the
 * defect this shape exists to make impossible.
 */
type CostMetric = Exclude<BudgetMetric, 'silence_minutes'>;

export interface FormValues {
  name: string;
  kind: BudgetKind;
  /** The metric for a cost rule. A cadence rule's metric is implied, never
   *  stored here — see `CostMetric` and `metricFor`. */
  metric: CostMetric;
  /** Kept as a string so a half-typed number never becomes NaN mid-edit. */
  threshold: string;
  period: BudgetPeriod;
  /** Blank means unscoped (every producer). */
  scope_producer: string;
  webhook_url: string;
  /** Blank means "leave whatever is stored alone". */
  webhook_secret: string;
  enabled: boolean;
}

/** Only the user-selectable metrics; `silence_minutes` is deliberately absent,
 *  so it can never be an <option>. */
const METRIC_LABELS: Record<CostMetric, string> = {
  cost_usd: 'cost (USD)',
  total_tokens: 'total tokens',
  turn_count: 'turn count',
};

/**
 * The ONE place a submitted `metric` is decided, and the cadence half of the
 * biconditional. A cadence rule's metric is implied by its kind, so it is
 * derived here instead of being selected — the metric <select> is not even
 * rendered for cadence.
 */
export function metricFor(kind: BudgetKind, metric: CostMetric): BudgetMetric {
  return kind === 'cadence' ? 'silence_minutes' : metric;
}

/** Field-keyed messages; an empty object means valid. */
export function validateForm(values: FormValues): Record<string, string> {
  const errors: Record<string, string> = {};

  if (values.name.length < 1) errors.name = 'Name is required.';
  else if (values.name.length > NAME_MAX) errors.name = `Name must be at most ${NAME_MAX} characters.`;

  const threshold = Number(values.threshold);
  if (values.threshold.trim() === '' || !Number.isFinite(threshold) || threshold <= 0) {
    errors.threshold = 'Threshold must be a number greater than 0.';
  } else if (
    values.kind === 'cadence'
    && (threshold < CADENCE_THRESHOLD_MIN_MINUTES || threshold > CADENCE_THRESHOLD_MAX_MINUTES)
  ) {
    // Bounds only — NO integer requirement, matching `withinCadenceBounds`
    // server-side: a declared cadence of 90.5 minutes is odd but honorable.
    errors.threshold = `Silence threshold must be ${CADENCE_THRESHOLD_MIN_MINUTES}–`
      + `${CADENCE_THRESHOLD_MAX_MINUTES} minutes.`;
  }

  if (values.scope_producer.length > SCOPE_PRODUCER_MAX) {
    errors.scope_producer = `Producer scope must be at most ${SCOPE_PRODUCER_MAX} characters.`;
  } else if (PRODUCER_FORBIDDEN_CHARS.test(values.scope_producer)) {
    errors.scope_producer = 'Producer scope must be an identity id: no spaces and none of # / = * : { }.';
  }

  if (values.webhook_url !== '') {
    let ok = false;
    try { ok = new URL(values.webhook_url).protocol === 'https:'; } catch { ok = false; }
    if (!ok) errors.webhook_url = 'Webhook URL must be a valid https:// URL.';
  }

  if (values.webhook_secret !== ''
    && (values.webhook_secret.length < SECRET_MIN || values.webhook_secret.length > SECRET_MAX)) {
    errors.webhook_secret = `Webhook secret must be ${SECRET_MIN}–${SECRET_MAX} characters.`;
  }

  return errors;
}

export function toCreateInput(values: FormValues): BudgetCreateInput {
  return {
    name: values.name,
    kind: values.kind,
    // Derived, never copied: this is the cadence half of the biconditional.
    metric: metricFor(values.kind, values.metric),
    threshold: Number(values.threshold),
    // `period` is required IFF kind === 'period', and REJECTED otherwise —
    // `cadence` falls into the rejecting branch with `run`, no special case.
    ...(values.kind === 'period' ? { period: values.period } : {}),
    ...(values.scope_producer !== '' ? { scope_producer: values.scope_producer } : {}),
    ...(values.webhook_url !== '' ? { webhook_url: values.webhook_url } : {}),
    ...(values.webhook_secret !== '' ? { webhook_secret: values.webhook_secret } : {}),
    enabled: values.enabled,
  };
}

/**
 * A field the user BLANKED that previously held a value must be sent as `null`
 * (→ `REMOVE` server-side, spec §6.3). Merely OMITTING it would silently leave
 * the old value in place — and for `webhook_url` that means a webhook the user
 * just deleted from the form keeps receiving alerts. Blank with nothing stored
 * stays omitted, so a patch never carries a no-op REMOVE.
 *
 * `toCreateInput` is unchanged: create omits, because there is nothing to clear.
 */
function clearable<K extends string>(
  key: K,
  value: string,
  previous: string | undefined,
): Partial<Record<K, string | null>> {
  if (value !== '') return { [key]: value } as Record<K, string>;
  if (previous !== undefined && previous !== '') return { [key]: null } as Record<K, null>;
  return {};
}

export function toPatchInput(values: FormValues, existing?: BudgetRuleView): BudgetPatchInput {
  return {
    name: values.name,
    threshold: Number(values.threshold),
    // Clears only a stored PRODUCER scope. A retired `scope_platform` cannot be
    // cleared by any patch (the server refuses the field even as null), so it is
    // never named here.
    ...clearable('scope_producer', values.scope_producer, existing?.scope_producer),
    ...clearable('webhook_url', values.webhook_url, existing?.webhook_url),
    // Only send a secret the user actually typed — blank leaves the stored one.
    // `webhook_secret` is deliberately NOT null-clearable (spec §6.3).
    ...(values.webhook_secret !== '' ? { webhook_secret: values.webhook_secret } : {}),
    enabled: values.enabled,
  };
}

function initialValues(existing?: BudgetRuleView): FormValues {
  return {
    name: existing?.name ?? '',
    kind: existing?.kind ?? 'run',
    // A cadence rule's stored metric is `silence_minutes`, which is not a
    // `CostMetric` and has no selector to populate — `metric` is immutable
    // server-side and the field is hidden on a cadence edit, so the unused
    // placeholder is correct rather than lossy.
    metric: existing && existing.metric !== 'silence_minutes' ? existing.metric : 'cost_usd',
    threshold: existing ? String(existing.threshold) : '',
    period: existing?.period ?? 'month',
    // Deliberately NOT seeded from a legacy `scope_platform`: a platform name
    // posted as a producer would scope the rule to an identity that does not exist.
    scope_producer: existing?.scope_producer ?? '',
    webhook_url: existing?.webhook_url ?? '',
    webhook_secret: '',
    enabled: existing?.enabled ?? true,
  };
}

export default function BudgetForm({
  existing,
  onDone,
  onCancel,
}: {
  existing?: BudgetRuleView;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<FormValues>(() => initialValues(existing));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const create = useCreateBudget();
  const update = useUpdateBudget();
  const editing = Boolean(existing);
  const pending = create.isPending || update.isPending;

  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    const found = validateForm(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    try {
      // `existing` is threaded in so a blanked field that HELD a value clears
      // rather than being silently omitted.
      if (existing) await update.mutateAsync({ ruleId: existing.rule_id, patch: toPatchInput(values, existing) });
      else await create.mutateAsync(toCreateInput(values));
      onDone();
    } catch (err) {
      // The server is authoritative — surface its code without losing the form.
      setServerError(err instanceof Error ? err.message : String(err));
    }
  };

  const fieldError = (key: string) =>
    errors[key] ? <span className="budget-form-error" role="alert">{errors[key]}</span> : null;

  return (
    <form className="card budget-form" onSubmit={submit} aria-label={editing ? 'Edit budget rule' : 'New budget rule'}>
      <label className="budget-field">
        <span>Name</span>
        {/* Explicit aria-label: fieldError renders inside this <label>, so the
            wrapper's computed accessible name would include the error text once
            validation fails. */}
        <input aria-label="Name" type="text" value={values.name} onChange={(e) => set('name', e.target.value)} />
        {fieldError('name')}
      </label>

      <label className="budget-field">
        <span>Kind</span>
        <select
          value={values.kind}
          disabled={editing}
          onChange={(e) => set('kind', e.target.value as BudgetKind)}
        >
          <option value="run">run — one session</option>
          <option value="period">period — calendar window</option>
          <option value="cadence">cadence — ingest silence</option>
        </select>
      </label>

      {values.kind === 'period' && (
        <label className="budget-field">
          <span>Period</span>
          <select
            value={values.period}
            disabled={editing}
            onChange={(e) => set('period', e.target.value as BudgetPeriod)}
          >
            <option value="day">day (UTC)</option>
            <option value="month">month (UTC)</option>
          </select>
        </label>
      )}

      {/* No metric selector for cadence: the metric is IMPLIED by the kind
          (`silence_minutes`), and offering a choice is what would let the form
          submit a pair the server rejects. Hidden, not policed. */}
      {values.kind !== 'cadence' && (
        <label className="budget-field">
          <span>Metric</span>
          <select
            value={values.metric}
            disabled={editing}
            onChange={(e) => set('metric', e.target.value as CostMetric)}
          >
            {(Object.keys(METRIC_LABELS) as CostMetric[]).map((m) => (
              <option key={m} value={m}>{METRIC_LABELS[m]}</option>
            ))}
          </select>
        </label>
      )}

      <label className="budget-field">
        <span>{values.kind === 'cadence' ? 'Threshold (minutes of silence)' : 'Threshold'}</span>
        {/* Explicit aria-label: same fieldError-inside-<label> issue as Name.
            It stays the constant "Threshold" across kinds so the field remains
            addressable by one name; the kind-specific wording and the bounds
            are in the visible text. */}
        <input aria-label="Threshold" type="text" inputMode="decimal" value={values.threshold}
          onChange={(e) => set('threshold', e.target.value)} />
        {values.kind === 'cadence' && (
          <span className="note">
            Maximum tolerated ingest silence, in minutes:{' '}
            {CADENCE_THRESHOLD_MIN_MINUTES}–{CADENCE_THRESHOLD_MAX_MINUTES}
            {' '}(1 hour – 14 days).
          </span>
        )}
        {fieldError('threshold')}
      </label>

      <label className="budget-field">
        <span>Producer scope</span>
        {/* Explicit aria-label: same fieldError-inside-<label> issue as Name. */}
        <input aria-label="Producer scope" type="text" placeholder="all producers" value={values.scope_producer}
          onChange={(e) => set('scope_producer', e.target.value)} />
        {fieldError('scope_producer')}
      </label>
      {typeof existing?.scope_platform === 'string' && (
        <p className="note budget-scope-retired">
          This rule is scoped by the retired platform “{existing.scope_platform}” and is not
          evaluated. A platform scope cannot be removed by an edit — delete this rule and
          create it again scoped by producer.
        </p>
      )}

      <label className="budget-field">
        <span>Webhook URL</span>
        {/* Explicit aria-label: same fieldError-inside-<label> issue as Name. */}
        <input aria-label="Webhook URL" type="text" placeholder="https://…" value={values.webhook_url}
          onChange={(e) => set('webhook_url', e.target.value)} />
        {fieldError('webhook_url')}
      </label>

      <label className="budget-field">
        <span>Webhook secret</span>
        {/* Explicit aria-label: the stored-secret hint below lives inside this
            <label>, so the wrapper's text alone would not name the field. */}
        <input aria-label="Webhook secret" type="password" autoComplete="new-password" value={values.webhook_secret}
          onChange={(e) => set('webhook_secret', e.target.value)} />
        {existing?.webhook_secret_set && (
          <span className="note">A secret is stored. Leave blank to keep it.</span>
        )}
        {fieldError('webhook_secret')}
      </label>

      <label className="budget-field budget-field-inline">
        <input type="checkbox" checked={values.enabled}
          onChange={(e) => set('enabled', e.target.checked)} />
        <span>Enabled</span>
      </label>

      {serverError && <p className="error" role="alert">Save failed: {serverError}</p>}

      <div className="budget-form-actions">
        <button type="submit" className="btn" disabled={pending}>
          {editing ? 'Save changes' : 'Create rule'}
        </button>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
